import { ExpandMore } from '@mui/icons-material'
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import cls from 'classnames'
import { memo, useRef } from 'react'

import Button from '@/components/base/Button'
import useTrans from '@/hooks/useTrans'

import AddressWithCode from './AddressWithCode'
import SelectTokenItem from './SelectTokenItem'
import { selectStyles } from './styles'
import { FlowDepositCodeViewProps } from './types'

const View = ({
  selectedToken,
  selectedNetwork,
  onChangeToken,
  isOpenMenu,
  setIsOpenMenu,
  networkOptions,
  isLoading,
  onChangeNetwork,
  addressToGenQR,
  onNext,
  tokenOptions,
}: FlowDepositCodeViewProps) => {
  const trans = useTrans()
  const anchorElRef = useRef<HTMLButtonElement>(null)
  const isShowMenuNetwork = networkOptions.length > 0

  return (
    <Stack>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography className="text-sm" data-testid="desc-from-platform">
          {trans.wrapped_token.please_send}
        </Typography>
        <Select
          variant="standard"
          sx={selectStyles}
          IconComponent={ExpandMore}
          value={selectedToken.symbol}
          onChange={onChangeToken}
          className="rounded-[80px] overflow-hidden"
          renderValue={(symbol) => {
            return <SelectTokenItem token={tokenOptions.find(tokenOpt => tokenOpt.symbol === symbol)} />
          }}
          data-testid="select-coin"
        >
          {tokenOptions.map((token) => (
            <MenuItem key={token.symbol} value={token.symbol} className="p-0">
              <SelectTokenItem token={token} />
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" className="my-2">
        <Typography className="text-sm">{trans.wrapped_token.through_network}</Typography>
        <MuiButton
          ref={anchorElRef}
          onClick={() => isShowMenuNetwork && setIsOpenMenu(true)}
          className={cls('cursor-pointer normal-case rounded-[8px] pl-3 pr-1', {
            'bg-gradient-btn': !selectedNetwork,
          })}
          data-testid="btn-select-network"
        >
          {selectedNetwork ? (
            <Typography className="text-sm font-bold">
              {selectedNetwork.chain_name_abbr} {selectedNetwork.layer_name && `(${selectedNetwork.layer_name})`}
            </Typography>
          ) : (
            <Typography className="text-sm">{trans.wrapped_token.select_network}</Typography>
          )}
          {isShowMenuNetwork && <ExpandMore />}
        </MuiButton>
        {
          isShowMenuNetwork && (
            <Menu anchorEl={anchorElRef.current} open={isOpenMenu} onClose={() => setIsOpenMenu(false)}>
              {networkOptions.map((network) => (
                <MenuItem
                  key={network.chain_name}
                  value={network.chain_name}
                  className="p-0"
                  onClick={() => onChangeNetwork(network)}
                  data-testid="menu-network-item"
                >
                  <Typography className="px-3 py-2 text-sm font-bold">
                    {network.chain_name} {network.layer_name && `(${network.layer_name})`}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          )
        }
      </Box>

      {!addressToGenQR && isLoading && (
        <Box display="flex" justifyContent="center" className="py-6" data-testid="circular-progress">
          <CircularProgress size="3rem" />
        </Box>
      )}

      {addressToGenQR && (
        <>
          <AddressWithCode address={addressToGenQR} loading={isLoading} />
          <Button
            onClick={onNext}
            loading={isLoading}
            data-testid="btn-confirm-deposit"
            size="xl"
            className="mt-5"
          >{trans.wrapped_token.btn_deposited(selectedToken.displaySymbol)}</Button>
        </>
      )}
    </Stack>
  )
}

export default memo(View)
